import { useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";

import { Modal } from "@/components/modal/modal";
import {
  Button,
  FormLine,
  FormSize,
  FormThemeProvider,
  PictureInput,
  SubmitButton,
  TextInput,
} from "ui/ecosystems/forms";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { openSupportChat } from "libs/support";
import { Link } from "ui/Link";

import {
  formSubmitted,
  NotificationAccountType,
  platformTypeOtherChanged,
  profilePictureDeleted,
  profilePictureFileSelected,
  userNameChanged,
} from "./model";
import {
  dispatchAppAction,
  selectSocialConnectNotificationState,
  socialAccountConnectActions,
} from "../../redux-model";

interface Props {
  type: NotificationAccountType;
  show: boolean;

  onHide(): void;

  onBack(): void;
}

export function NotificationAccountConnectDialog(props: Props) {
  const { userName, profilePictureUrl, platformTypeOther, isConnecting } =
    useSelector(selectSocialConnectNotificationState);

  useEffect(() => {
    if (!props.show) {
      return;
    }

    dispatchAppAction(
      socialAccountConnectActions.setNotificationType(props.type)
    );

    return () => {
      dispatchAppAction(
        socialAccountConnectActions.resetNotificationFormState()
      );
    };
  }, [props.show, props.type]);

  const formDisabled = isConnecting;

  return (
    <TikTokConnectDialogRoot show={props.show} onHide={props.onHide}>
      <Modal.Header closeButton onHide={props.onHide}>
        <Modal.Title>
          {props.type === "other"
            ? "Connect a notification account"
            : `Connect a ${PlatformNameByTypeMap[props.type]} account`}
        </Modal.Title>
        <Subtitle>
          Get email and/or push notifications to alert you to posts that need to
          go live. Need help on how to set things up?{" "}
          <Link as="button" onClick={() => openSupportChat("", "plans")}>
            Ask live chat
          </Link>
          .
        </Subtitle>
      </Modal.Header>
      <FormThemeProvider size={FormSize.medium}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formSubmitted();
          }}
        >
          <Modal.Body>
            <FormContent>
              <FormLine label="Profile Picture">
                <PictureInput
                  value={profilePictureUrl}
                  onChange={profilePictureFileSelected}
                  onRemove={profilePictureDeleted}
                  disabled={formDisabled}
                />
              </FormLine>
              <Info>
                <FormLine label="Username">
                  <TextInput
                    placeholder="@username"
                    value={userName}
                    onChange={userNameChanged}
                    disabled={formDisabled}
                  />
                </FormLine>
                {props.type === "other" && (
                  <FormLine label={<>What platform this is for?</>}>
                    <Select
                      value={platformTypeOther}
                      onValueChange={platformTypeOtherChanged}
                      disabled={formDisabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Other platforms" />
                      </SelectTrigger>
                      <SelectContent>
                        {platformOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormLine>
                )}
              </Info>
            </FormContent>

            <Actions>
              <Button
                type="button"
                text
                size={FormSize.large}
                onClick={props.onBack ? props.onBack : undefined}
              >
                Back
              </Button>
              <SubmitButton
                isSubmitting={isConnecting}
                primary
                size={FormSize.small}
                style={{ minWidth: 110 }}
              >
                Connect {PlatformNameByTypeMap[props.type]}
              </SubmitButton>
            </Actions>
          </Modal.Body>
        </form>
      </FormThemeProvider>
    </TikTokConnectDialogRoot>
  );
}

const PlatformNameByTypeMap = {
  tiktok: "TikTok",
  threads: "Threads",
  snapchat: "Snapchat",
  other: "Other",
};

const platformOptions = [
  "Email campaign",
  "Wechat",
  "TikTok",
  "Twitch",
  "Threads",
  "Spotify",
  "Tumblr",
  "Whatsapp",
  "Viber",
  "Line",
  "Medium",
  "Blog",
  "Other",
];

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  font-size: 14px;
  color: #454545;
`;

const Info = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 22px;
`;

const FormContent = styled.form`
  display: grid;
  grid-template-columns: 1fr 2fr;
  grid-gap: 75px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TikTokConnectDialogRoot = styled(Modal)`
  width: 622px;

  ${Actions} {
    margin-top: 58px;
  }
`;
